import moment from "moment";
import { ResidencePermit, ResidencePermitType } from "../../models/ResidencePermit";
import { Result } from "../../models/Result";

// The residence permit's business rules (TNPDEVAPPS-1953/1954), kept out of the component so they
// can be tested on their own (residencePermitRules.test.ts). See docs/features/residence-permit-scorecard.md.

// Residence permit field rules. The backend returns 400 errors as one flat list (errors.Error), so
// per-field messages come from this client-side pass; the backend stays authoritative, including the
// EU/EEA issuing-country rule.
export const FOREIGNER_PERSONAL_NUMBER_REGEX = /^[0-9A-Z]{8,16}([-/][0-9A-Z]{2,6})?$/;
// Longest value the regex accepts: 16 + separator + 6.
export const MAX_FOREIGNER_PERSONAL_NUMBER_LENGTH = 23;
export const PERMIT_DOCUMENT_NUMBER_REGEX = /^[A-Za-z0-9-]{2,30}$/;
export const MAX_ISSUING_AUTHORITY_LENGTH = 128;
export const MIN_PERMIT_DATE_OF_ISSUE = new Date(1900, 0, 1);
export const MIN_RESIDENCE_PERMIT_REASON_LENGTH = 5;
export const MAX_RESIDENCE_PERMIT_REASON_LENGTH = 500;

// The scorecard's Approve tooltip and the section's banner use the same sentences, so they can never
// drift apart.
export const NO_RESIDENCE_PERMIT_MESSAGE = "Complete residence permit entry before approving";
export const RESIDENCE_PERMIT_LOADING_MESSAGE = "Loading residence permit data…";
export const RESIDENCE_PERMIT_LOAD_FAILED_MESSAGE = "Residence permit data could not be loaded";
export const ISSUING_COUNTRIES_UNAVAILABLE_MESSAGE = "The EU/EEA country list could not be loaded.";
export const RESIDENCE_PERMIT_LOAD_FAILED_TOAST = "Residence permit data could not be loaded. Use Retry.";
export const RESIDENCE_PERMIT_SAVE_FAILED_TOAST = "The residence permit could not be saved. Check the connection and press Done again.";
export const RESIDENCE_PERMIT_STATUS_SAVE_FAILED_TOAST = "Residence permit status could not be saved. Check the connection and save again.";

// The statuses the residence permit endpoints answer with CRM's own sentences: 400 (validation) and
// 409 (closed submission, missing scan, EU/EEA national, 90-day validity).
const CRM_MESSAGE_STATUSES = [400, 409];

// What the operator reads when a residence permit request fails. CRM's message is shown only for a
// 400 or 409. Anything else is not a sentence meant for the operator: with no response (the 15 s
// timeout, a network failure) HttpService returns axios's raw text ("timeout of 15000ms exceeded",
// "Network Error"), and a body that is not CRM's error shape (an ingress 502 HTML page, ProblemDetails
// JSON) comes back stringified. That goes to the console and the operator gets the fallback instead.
export const residencePermitFailureMessage = (result: Pick<Result<unknown>, "message" | "status">, fallback: string): string => {
    if (result.status !== undefined && CRM_MESSAGE_STATUSES.includes(result.status) && result.message) {
        return result.message;
    }
    console.error(`Residence permit request failed (status ${result.status ?? "none"}):`, result.message);
    return fallback;
};

// Where the scorecard is with the section's GET: not answered yet, answered, or failed.
export type ResidencePermitLoadState = "loading" | "loaded" | "failed";

export interface ResidencePermitDraft {
    permitType: ResidencePermitType | null;
    foreignerPersonalNumber: string;
    documentNumber: string;
    issuingCountryCode: string;
    issuingAuthority: string;
    dateOfIssue: Date | null;
    dateOfExpiry: Date | null;
    noExpirationDate: boolean;
    isActive: boolean;
    // Stored on every permit row; CRM compares it with the other fields (MatchesRequest).
    reason: string;
}

export type ResidencePermitErrors = Partial<Record<keyof ResidencePermitDraft, string>>;

export const EMPTY_RESIDENCE_PERMIT_DRAFT: ResidencePermitDraft = {
    permitType: null,
    foreignerPersonalNumber: "",
    documentNumber: "",
    issuingCountryCode: "",
    issuingAuthority: "",
    dateOfIssue: null,
    dateOfExpiry: null,
    noExpirationDate: false,
    isActive: true,
    reason: "",
};

// Date-only values must never go through toISOString()/new Date("yyyy-MM-dd"): both are UTC, and in
// UTC+2/+3 they shift the day back by one. Build and read local calendar dates explicitly instead.
// Sent as plain "yyyy-MM-dd", which is what CRM's SaveResidencePermitRequest asks for.
export const toDateOnlyString = (date: Date): string => moment(date).format("YYYY-MM-DD");

export const fromDateOnlyString = (value: string | null | undefined): Date | null => {
    if (!value || value.length < 10) {
        return null;
    }
    const [year, month, day] = value.substring(0, 10).split("-").map(Number);
    return new Date(year, month - 1, day);
};

export const formatDateOnly = (value: string | null | undefined): string => {
    const date = fromDateOnlyString(value);
    return date ? moment(date).format("DD/MM/YYYY") : "";
};

// The backend compares Date of Issue to the UTC date. A local "today" between 00:00 and 03:00 in
// Bulgaria is still yesterday in UTC, so the calendar would offer a day the server rejects. This is
// that UTC day as a local midnight, which is what maxDate and the "not in the future" check both use.
export const startOfToday = (now: Date = new Date()): Date => new Date(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());

// A client outside the EU/EEA cannot be approved without an active residence permit: the first
// record from the GET must exist and be active (owner decision, October 2026). EU/EEA nationals
// need no permit. Until the GET has answered this is true as well, so Approve never opens early.
// The one rule for the scorecard's Approve lock and the section's banner, so the banner never
// claims approval is blocked when it is not.
export const isResidencePermitApprovalBlocked = (isMemberOfEuEea: boolean, currentPermit: ResidencePermit | null): boolean =>
    !isMemberOfEuEea && currentPermit?.isActive !== true;

// Why Approve is locked by the permit: still loading, the GET failed (nothing the operator can fill
// in), or a permit is really missing.
export const residencePermitApprovalBlockedMessage = (loadState: ResidencePermitLoadState): string => {
    switch (loadState) {
        case "loading":
            return RESIDENCE_PERMIT_LOADING_MESSAGE;
        case "failed":
            return RESIDENCE_PERMIT_LOAD_FAILED_MESSAGE;
        default:
            return NO_RESIDENCE_PERMIT_MESSAGE;
    }
};

// The edit dialogue's checks on "Done", run only when the permit data was changed. Every rule CRM's
// SaveResidencePermitRequestValidator enforces shows under its field first. today is the UTC day
// (startOfToday) and is a parameter only so the tests can fix it.
export const validateResidencePermitDraft = (draft: ResidencePermitDraft, today: Date = startOfToday()): ResidencePermitErrors => {
    const errors: ResidencePermitErrors = {};

    if (draft.permitType == null) {
        errors.permitType = "Permit Type is required.";
    }

    if (!FOREIGNER_PERSONAL_NUMBER_REGEX.test(draft.foreignerPersonalNumber)) {
        errors.foreignerPersonalNumber = "Foreigner's Personal Number must be 8 to 16 capital letters or digits, optionally followed by - or / and 2 to 6 more.";
    }

    if (!PERMIT_DOCUMENT_NUMBER_REGEX.test(draft.documentNumber)) {
        errors.documentNumber = "Document Number must be 2 to 30 letters, digits or dashes.";
    }

    if (!draft.issuingCountryCode) {
        errors.issuingCountryCode = "Issuing Country is required.";
    }

    const issuingAuthority = draft.issuingAuthority.trim();
    if (!issuingAuthority) {
        errors.issuingAuthority = "Issuing Authority is required.";
    } else if (issuingAuthority.length > MAX_ISSUING_AUTHORITY_LENGTH) {
        errors.issuingAuthority = `Issuing Authority must be at most ${MAX_ISSUING_AUTHORITY_LENGTH} characters.`;
    }

    if (!draft.dateOfIssue) {
        errors.dateOfIssue = "Date of Issue is required.";
    } else if (draft.dateOfIssue < MIN_PERMIT_DATE_OF_ISSUE) {
        errors.dateOfIssue = "Date of Issue must be on or after 01/01/1900.";
    } else if (draft.dateOfIssue > today) {
        errors.dateOfIssue = "Date of Issue cannot be in the future.";
    }

    // A missing expiry needs "No expiration date"; a set one must be strictly after the issue
    // date (CRM compares expiry.Date > issue.Date, so the same day is rejected).
    if (!draft.noExpirationDate) {
        if (!draft.dateOfExpiry) {
            errors.dateOfExpiry = "Date of Expiry is required unless No expiration date is checked.";
        } else if (draft.dateOfIssue && draft.dateOfExpiry <= draft.dateOfIssue) {
            errors.dateOfExpiry = "Date of Expiry must be after the Date of Issue.";
        }
    }

    // An active permit may not be expired; one that expires today still counts as valid.
    // An inactive permit may carry a past expiry date — it is kept as a record. Shown under the
    // date, because Active is locked while the permit being edited is active.
    if (!errors.dateOfExpiry && draft.isActive && !draft.noExpirationDate && draft.dateOfExpiry && draft.dateOfExpiry < today) {
        errors.dateOfExpiry = "This permit has expired. Set a Date of Expiry of today or later, or check No expiration date.";
    }

    const reason = draft.reason.trim();
    if (reason.length < MIN_RESIDENCE_PERMIT_REASON_LENGTH || reason.length > MAX_RESIDENCE_PERMIT_REASON_LENGTH) {
        errors.reason = `Reason must be between ${MIN_RESIDENCE_PERMIT_REASON_LENGTH} and ${MAX_RESIDENCE_PERMIT_REASON_LENGTH} characters long.`;
    }

    return errors;
};
